import React, { useState, useEffect, useCallback } from 'react';
import teamService from '../services/teamService';
import { useToast } from '../contexts/ToastContext';
import './Feeds.css';

// Team Management — the client admin configures who has VMS access and which
// of the 4 VMS roles (Admin, Operator, Packer, RTO) they hold. Backed by the
// QuirkyQode org-users API via /api/auth/team, same pattern as the B2B Tool.
const ROLE_OPTIONS = [
    { value: 'admin', label: 'Admin' },
    { value: 'operator', label: 'Operator' },
    { value: 'packer', label: 'Packer' },
    { value: 'rto', label: 'RTO' }
];

const AdminUserManagement = () => {
    const toast = useToast();
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // create, edit
    const [selectedMember, setSelectedMember] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        role: 'operator'
    });
    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const fetchTeam = useCallback(async () => {
        try {
            setLoading(true);
            const result = await teamService.getTeam();
            if (result.success) {
                setMembers(result.data);
            }
        } catch (error) {
            console.error('Error fetching team:', error);
            toast.error(error.response?.data?.message || 'Failed to load team. Make sure you are the account owner.');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchTeam();
    }, [fetchTeam]);

    const openCreateModal = () => {
        setModalMode('create');
        setSelectedMember(null);
        setFormData({ name: '', email: '', password: '', role: 'operator' });
        setFormErrors({});
        setShowModal(true);
    };

    const openEditModal = (member) => {
        setModalMode('edit');
        setSelectedMember(member);
        setFormData({
            name: member.name,
            email: member.email,
            password: '',
            role: member.vms_role || 'operator'
        });
        setFormErrors({});
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedMember(null);
        setFormErrors({});
    };

    const validateForm = () => {
        const errors = {};

        if (modalMode === 'create') {
            if (!formData.name.trim()) errors.name = 'Full name is required';
            if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Valid email is required';
            if (!formData.password || formData.password.length < 6) errors.password = 'Password must be at least 6 characters';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setSubmitting(true);
            let result;

            if (modalMode === 'create') {
                result = await teamService.createMember(formData);
            } else {
                result = await teamService.updateMember(selectedMember._id, { role: formData.role });
            }

            if (result.success) {
                toast.success(modalMode === 'create' ? 'Team member added successfully' : 'Role updated successfully');
                closeModal();
                fetchTeam();
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Operation failed');
        } finally {
            setSubmitting(false);
        }
    };

    const toggleMemberStatus = async (member) => {
        try {
            const result = await teamService.updateMember(member._id, { is_active: !member.is_active });
            if (result.success) {
                fetchTeam();
                toast.success(member.is_active ? 'Team member deactivated' : 'Team member activated');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update status');
        }
    };

    const removeMember = async (member) => {
        if (!window.confirm(`Remove ${member.name} from VMS? They will lose all access.`)) return;
        try {
            const result = await teamService.deleteMember(member._id);
            if (result.success) {
                fetchTeam();
                toast.success('Team member removed');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to remove team member');
        }
    };

    const roleLabel = (role) => ROLE_OPTIONS.find(r => r.value === role)?.label || 'No Access';

    return (
        <div className="feeds-page">
            {/* Filters & Actions */}
            <div className="filters-section">
                <div className="filters-grid">
                    <div className="filter-group filter-actions">
                        <button className="btn btn-primary" onClick={openCreateModal}>
                            <i className="fas fa-plus" style={{ marginRight: '8px' }}></i> Add Team Member
                        </button>
                    </div>
                </div>
            </div>

            {/* Team Table */}
            <div className="table-section">
                {loading ? (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Loading team...</p>
                    </div>
                ) : members.length === 0 ? (
                    <div className="empty-state">
                        <i className="fas fa-users-slash" style={{ fontSize: '48px', opacity: 0.2, marginBottom: '16px' }}></i>
                        <p>No team members yet. Add your first team member above.</p>
                    </div>
                ) : (
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>VMS Role</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {members.map(member => (
                                <tr key={member._id}>
                                    <td>{member.name}</td>
                                    <td>{member.email}</td>
                                    <td>
                                        <span className={`role-badge ${member.vms_role || ''}`}>
                                            {roleLabel(member.vms_role)}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`status-badge ${member.is_active ? 'active' : 'inactive'}`}>
                                            {member.is_active ? 'active' : 'inactive'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="action-btns">
                                            <button
                                                className="btn btn-sm btn-secondary"
                                                onClick={() => openEditModal(member)}
                                                title="Edit Role"
                                            >
                                                <i className="fas fa-edit"></i>
                                            </button>
                                            <button
                                                className={`btn btn-sm ${member.is_active ? 'btn-danger' : 'btn-success'}`}
                                                onClick={() => toggleMemberStatus(member)}
                                                title={member.is_active ? 'Deactivate' : 'Activate'}
                                            >
                                                <i className={`fas ${member.is_active ? 'fa-user-slash' : 'fa-user-check'}`}></i>
                                            </button>
                                            <button
                                                className="btn btn-sm btn-danger"
                                                onClick={() => removeMember(member)}
                                                title="Remove"
                                            >
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Team Member Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{modalMode === 'create' ? 'Add Team Member' : 'Edit Role'}</h2>
                            <button className="close-btn" onClick={closeModal}>✕</button>
                        </div>
                        <div className="modal-body">
                            {modalMode === 'create' && (
                                <>
                                    <div className="form-group">
                                        <label>Full Name</label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            placeholder="Jane Smith"
                                        />
                                        {formErrors.name && <span className="form-error">{formErrors.name}</span>}
                                    </div>
                                    <div className="form-group">
                                        <label>Email</label>
                                        <input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            placeholder="jane@company.com"
                                        />
                                        {formErrors.email && <span className="form-error">{formErrors.email}</span>}
                                    </div>
                                    <div className="form-group">
                                        <label>Password</label>
                                        <input
                                            type="password"
                                            value={formData.password}
                                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                            placeholder="Min. 6 characters"
                                        />
                                        {formErrors.password && <span className="form-error">{formErrors.password}</span>}
                                    </div>
                                </>
                            )}

                            {modalMode === 'edit' && (
                                <p style={{ marginBottom: '16px', color: 'var(--text-muted, #666)' }}>
                                    Update the VMS role for <strong>{selectedMember?.name}</strong>
                                </p>
                            )}

                            <div className="form-group">
                                <label>VMS Role</label>
                                <select
                                    value={formData.role}
                                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                >
                                    {ROLE_OPTIONS.map(r => (
                                        <option key={r.value} value={r.value}>{r.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={closeModal}>
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleSubmit}
                                disabled={submitting}
                            >
                                {submitting ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminUserManagement;
